import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Netzbetreiber, UniqueTeilgebietAndNetzbetreibernummer } from '../../domain/model/netzbetreiber';
import { map, shareReplay } from 'rxjs/operators';
import { NetzzuordnungSnapshot } from '../../domain/model/netzzuordnung-snapshot';
import { DashboardInfoContainer } from '../../domain/model/dashboard-info-container';
import { AuthenticationService } from '../auth/authentication.service';
import { Subject } from 'rxjs/internal/Subject';

@Injectable({
    providedIn: 'root',
})
export class NetzbetreiberService {
    public refreshRequested$ = new Subject<void>();

    private http = inject(HttpClient);
    private authService = inject(AuthenticationService);

    private readonly VALID_CACHE_TIME: number = 1000 * 5; // 5 seconds
    private readonly CACHE_SIZE = 1;

    private cache$: Observable<Netzbetreiber[]>;
    private CACHE_CALLED: number = 0;

    private static toHttpParams(netzbetreibernummer: string): HttpParams {
        let params = new HttpParams();

        if (netzbetreibernummer) {
            params = params.set('netzbetreibernummer', netzbetreibernummer);
        }

        return params;
    }

    public getAlleNetzbetreiber(netzbetreibernummer?: string): Observable<Netzbetreiber[]> {
        const params = NetzbetreiberService.toHttpParams(netzbetreibernummer);
        return this.http.get(environment.API_URL + '/netzbetreiber', { params }) as Observable<Netzbetreiber[]>;
    }

    public getCachedNetzbetreiber(): Observable<Netzbetreiber[]> {
        const now = Date.now();
        if (!this.cache$ || now - this.CACHE_CALLED > this.VALID_CACHE_TIME) {
            this.CACHE_CALLED = now;
            this.cache$ = this.getAlleNetzbetreiber().pipe(
                shareReplay(this.CACHE_SIZE),
            );
        }

        return this.cache$;
    }

    public getAlleUniqueTeilgebietAndNetzbetreibernummer(unique: UniqueTeilgebietAndNetzbetreibernummer): Observable<UniqueTeilgebietAndNetzbetreibernummer[]> {
        return this.getCachedNetzbetreiber().pipe(
            map((nb: Netzbetreiber[]) => {
                return nb.filter(value => {
                    return value.teilgebiet?.trim()?.toLowerCase() === unique.teilgebiet?.trim()?.toLowerCase() &&
                        value.netzbetreibernummer?.trim()?.toLowerCase() === unique.netzbetreibernummer?.trim()?.toLowerCase();
                });
            }),
        );
    }

    public getNetzbetreiber(stadtwerkeId: string): Observable<Netzbetreiber> {
        return this.http.get(environment.API_URL + '/netzbetreiber/' + stadtwerkeId) as Observable<Netzbetreiber>;
    }

    public confirmUpToDateness(stadtwerkeId: string): Observable<Netzbetreiber> {
        let params = new HttpParams();
        params = params.set('stadtwerkeId', stadtwerkeId);
        return this.http.patch(environment.API_URL + '/netzbetreiber/confirmuptodateness', null, { params }) as Observable<Netzbetreiber>;
    }

    public saveNetzbetreiberKaskadenKontakt(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.put(environment.API_URL + '/netzbetreiber/' + netzbetreiber.stadtwerkeId + '/kaskadenkontakt',
            netzbetreiber) as Observable<Netzbetreiber>;
    }

    public saveNetzbetreiberAdresse(netzbetreiber: Netzbetreiber, sendNotification: boolean = false): Observable<Netzbetreiber> {
        const params = new HttpParams();
        params.set('sendNotification', `${sendNotification}`);
        return this.http.put(environment.API_URL + '/netzbetreiber/' + netzbetreiber.stadtwerkeId + '/adresse',
            netzbetreiber, { params }) as Observable<Netzbetreiber>;
    }

    public saveNetzbetreiberBasisdaten(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        const params = new HttpParams();
        return this.http.put(environment.API_URL + '/admin/netzbetreiber/' + netzbetreiber.stadtwerkeId + '/basisdaten',
            netzbetreiber, { params }) as Observable<Netzbetreiber>;
    }

    public createNetzbetreiber(netzbetreiber: Netzbetreiber): Observable<Netzbetreiber> {
        return this.http.post(environment.API_URL + '/admin/netzbetreiber',
            netzbetreiber) as Observable<Netzbetreiber>;
    }

    public deleteNetzbetreiber(netzbetreiber: Netzbetreiber): Observable<any> {
        return this.http.delete(environment.API_URL + '/admin/netzbetreiber/' + netzbetreiber.stadtwerkeId) as Observable<Netzbetreiber>;
    }

    public isStadtwerkeIdAvailable(stadtwerkeId: string): Observable<boolean> {
        return this.http.get(environment.API_URL + '/admin/netzbetreiber/stadtwerkeid/' + stadtwerkeId) as Observable<boolean>;
    }

    public getAvailableStadtwerkeId(): Observable<string> {
        return this.http.get(environment.API_URL + '/admin/netzbetreiber/available_stadtwerkeid',
            { responseType: 'text' }) as Observable<string>;
    }

    public updateLastUuidChangeConfirmDate(stwId: string, lastUuidChangeConfirmDate: Date): Observable<NetzzuordnungSnapshot> {
        return this.http.post<NetzzuordnungSnapshot>(
            `${environment.API_URL}/netzbetreiber/${stwId}/updateLastUuidChangeConfirmDate`,
            lastUuidChangeConfirmDate,
        );
    }

    public shouldDisplayErzeugerDetails(dashboardInfoDto: DashboardInfoContainer): boolean {
        return dashboardInfoDto.isErzeugungsAnlagenPflegeAktiv || this.authService.user.netzbetreiberType === 'WESTNETZ';
    }
}
